import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Instagram, Clock, CheckCircle, Navigation, Star, QrCode } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBooking } from '../contexts/BookingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Business & GPS
const GOOGLE_REVIEW_URL = "https://www.google.com/search?kgmid=/g/11y8tg20zx&q=IPRBC+KAMIT";
const GPS_COORDS = { lat: 32.6481, lng: -16.9058 };
const GOOGLE_MAPS_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${GPS_COORDS.lat},${GPS_COORDS.lng}&travelmode=driving`;
const QR_CODE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(GOOGLE_REVIEW_URL)}`;

const ContactPage = () => {
  const { language, t } = useLanguage();
  const { openBooking } = useBooking();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError(language === 'pt' 
        ? 'Erro ao enviar mensagem. Tente novamente.' 
        : 'Error sending message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-kamit-beige pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-kamit-brown">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl md:text-6xl text-kamit-beige mb-4">
            {t('contact_title')}
          </h1>
          <p className="text-kamit-beige/70 text-lg">
            {language === 'pt'
              ? 'Estamos aqui para ajudar. Entre em contacto connosco.'
              : 'We are here to help. Get in touch with us.'
            }
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl text-kamit-brown mb-8">
                {language === 'fr' ? 'Informations de Contact' : language === 'de' ? 'Kontaktinformationen' : language === 'pt' ? 'Informações de Contacto' : language === 'es' ? 'Información de Contacto' : 'Contact Information'}
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-kamit-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-kamit-brown mb-1">
                      {language === 'fr' ? 'Adresse' : language === 'de' ? 'Adresse' : language === 'pt' ? 'Morada' : language === 'es' ? 'Dirección' : 'Address'}
                    </h3>
                    <p className="text-kamit-brown/70">
                      Rua da Ponte Nova N° 27<br />
                      Loja A, Rés de Chão<br />
                      9050-440 Santa Luzia<br />
                      Madeira, Portugal
                    </p>
                    {/* GPS Navigation Button */}
                    <a 
                      href={GOOGLE_MAPS_DIRECTIONS}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-kamit-green text-white rounded-full text-sm font-medium hover:bg-kamit-green/90 transition-colors"
                    >
                      <Navigation className="h-4 w-4" />
                      {language === 'fr' ? 'Itinéraire GPS' : language === 'de' ? 'GPS-Navigation' : language === 'pt' ? 'Navegação GPS' : language === 'es' ? 'Navegación GPS' : 'Get Directions'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <Phone className="h-6 w-6 text-kamit-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-kamit-brown mb-1">
                      {language === 'pt' ? 'Telefone' : 'Phone'}
                    </h3>
                    <p className="text-kamit-brown/70">
                      <a href="tel:+351935984047" className="hover:text-kamit-gold transition-colors">
                        +351 935 984 047
                      </a>
                      <br />
                      <a href="tel:+351291650959" className="hover:text-kamit-gold transition-colors">
                        +351 291 650 959
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <Mail className="h-6 w-6 text-kamit-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-kamit-brown mb-1">Email</h3>
                    <a 
                      href="mailto:iprbckamit@gmail.com" 
                      className="text-kamit-brown/70 hover:text-kamit-gold transition-colors"
                    >
                      iprbckamit@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <Instagram className="h-6 w-6 text-kamit-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-kamit-brown mb-1">Instagram</h3>
                    <a 
                      href="https://instagram.com/kamitmadeira" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-kamit-brown/70 hover:text-kamit-gold transition-colors"
                    >
                      @kamitmadeira
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="h-6 w-6 text-kamit-gold" />
                  </div>
                  <div>
                    <h3 className="font-medium text-kamit-brown mb-1">
                      {language === 'fr' ? 'Horaires' : language === 'de' ? 'Öffnungszeiten' : language === 'pt' ? 'Horário' : language === 'es' ? 'Horario' : 'Hours'}
                    </h3>
                    <div className="text-kamit-brown/70 text-sm space-y-1">
                      <p>{language === 'fr' ? 'Lun, Mar, Mer, Ven, Sam' : language === 'de' ? 'Mo, Di, Mi, Fr, Sa' : language === 'pt' ? 'Seg, Ter, Qua, Sex, Sáb' : language === 'es' ? 'Lun, Mar, Mié, Vie, Sáb' : 'Mon, Tue, Wed, Fri, Sat'}: 9:00 - 20:00</p>
                      <p className="text-kamit-brown/50">
                        {language === 'fr' ? 'Jeudi & Dimanche' : language === 'de' ? 'Donnerstag & Sonntag' : language === 'pt' ? 'Quinta & Domingo' : language === 'es' ? 'Jueves & Domingo' : 'Thursday & Sunday'}: {language === 'fr' ? 'Fermé' : language === 'de' ? 'Geschlossen' : language === 'pt' ? 'Fechado' : language === 'es' ? 'Cerrado' : 'Closed'}
                      </p>
                      <p className="text-kamit-gold font-medium">
                        {language === 'fr' ? 'Jours fériés: Ouvert' : language === 'de' ? 'Feiertage: Geöffnet' : language === 'pt' ? 'Feriados: Aberto' : language === 'es' ? 'Festivos: Abierto' : 'Holidays: Open'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Book CTA */}
              <div className="mt-8 p-6 bg-kamit-cream rounded-xl">
                <h3 className="font-heading text-xl text-kamit-brown mb-4">
                  {language === 'fr' ? 'Préférez-vous réserver en ligne?' : language === 'de' ? 'Möchten Sie online buchen?' : language === 'pt' ? 'Prefere marcar online?' : language === 'es' ? '¿Prefiere reservar en línea?' : 'Prefer to book online?'}
                </h3>
                <Button 
                  onClick={() => openBooking()}
                  className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full"
                  data-testid="contact-book-btn"
                >
                  {t('nav_book')}
                </Button>
              </div>

              {/* QR Code for Google Reviews */}
              <div className="mt-8 p-6 bg-white rounded-xl shadow-sm border border-kamit-brown/10">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-kamit-gold fill-kamit-gold" />
                  <h3 className="font-heading text-xl text-kamit-brown">
                    {language === 'fr' ? 'Laissez-nous un avis' : language === 'de' ? 'Hinterlassen Sie eine Bewertung' : language === 'pt' ? 'Deixe-nos uma avaliação' : language === 'es' ? 'Déjenos una reseña' : 'Leave us a Review'}
                  </h3>
                </div>
                <p className="text-kamit-brown/70 text-sm mb-4">
                  {language === 'fr' ? 'Scannez ce QR code pour laisser un avis sur Google' : language === 'de' ? 'Scannen Sie diesen QR-Code, um eine Google-Bewertung abzugeben' : language === 'pt' ? 'Digitalize este código QR para deixar uma avaliação no Google' : language === 'es' ? 'Escanee este código QR para dejar una reseña en Google' : 'Scan this QR code to leave a Google review'}
                </p>
                <div className="flex justify-center">
                  <img 
                    src={QR_CODE_URL}
                    alt="QR Code for Google Reviews"
                    className="w-40 h-40 rounded-lg shadow-md"
                  />
                </div>
                <a 
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-4"
                >
                  <Button variant="outline" className="w-full border-kamit-gold text-kamit-gold hover:bg-kamit-gold hover:text-white rounded-full">
                    <Star className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Écrire un avis' : language === 'de' ? 'Bewertung schreiben' : language === 'pt' ? 'Escrever avaliação' : language === 'es' ? 'Escribir reseña' : 'Write a Review'}
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl text-kamit-brown mb-8">
                {language === 'pt' ? 'Envie-nos uma Mensagem' : 'Send Us a Message'}
              </h2>

              {isSubmitted ? (
                <div className="bg-kamit-green/10 border border-kamit-green/30 rounded-xl p-8 text-center">
                  <CheckCircle className="h-16 w-16 text-kamit-green mx-auto mb-4" />
                  <h3 className="font-heading text-2xl text-kamit-brown mb-2">
                    {language === 'pt' ? 'Mensagem Enviada!' : 'Message Sent!'}
                  </h3>
                  <p className="text-kamit-brown/70">
                    {language === 'pt'
                      ? 'Entraremos em contacto consigo em breve.'
                      : 'We will contact you soon.'
                    }
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-kamit-brown">
                        {language === 'pt' ? 'Nome' : 'Name'} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white border-kamit-brown/20 focus:border-kamit-gold"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-kamit-brown">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white border-kamit-brown/20 focus:border-kamit-gold"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-kamit-brown">
                        {language === 'pt' ? 'Telefone' : 'Phone'}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-white border-kamit-brown/20 focus:border-kamit-gold"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-kamit-brown">
                        {language === 'pt' ? 'Assunto' : 'Subject'} *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="bg-white border-kamit-brown/20 focus:border-kamit-gold"
                        data-testid="input-subject"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-kamit-brown">
                      {language === 'pt' ? 'Mensagem' : 'Message'} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-white border-kamit-brown/20 focus:border-kamit-gold resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-kamit-brown hover:bg-kamit-dark text-kamit-beige rounded-full py-6 text-lg"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <div className="animate-spin h-5 w-5 border-2 border-kamit-beige border-t-transparent rounded-full" />
                    ) : (
                      <>
                        {language === 'pt' ? 'Enviar Mensagem' : 'Send Message'}
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-kamit-cream">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3360.5!2d-16.89!3d32.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDM5JzAwLjAiTiAxNsKwNTMnMjQuMCJX!5e0!3m2!1sen!2spt!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="IPRBC KAMIT Location"
        />
      </section>
    </div>
  );
};

export default ContactPage;
